import { logger, safeError } from '../../utils/logger';
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { findAll, clearAuthCache } from '../../services/Service';
import {
  onLogin,
  setAuth,
  setRoles,
  setPermisos,
  setLinkPermisos,
} from '../Auth/UserLoginReducer';

interface AuthContextType {
  isAuthenticated: boolean | null;
  checkAuth: () => Promise<boolean | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const AuthProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<
    boolean | null
  >(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();

  const checkAuth = useCallback(async (): Promise<boolean | null> => {
    let isRedirecting = false;
    try {
      // Limpiar cache antes de verificar para asegurar datos frescos
      clearAuthCache();

      const statusResponse = await findAll('auth/status');
      logger.log('Status Response Status:', statusResponse?.status);
      logger.log('Status Response Data:', statusResponse?.data);

      if (statusResponse?.data?.authenticated) {
        logger.log('Usuario autenticado, cargando datos...');
        // Limpiar flag de verificación silenciosa al autenticarse correctamente
        sessionStorage.removeItem('silentCheckAttempted');

        const [authoritiesResponse, userResponse] = await Promise.all(
          [findAll('auth/authorities'), findAll('usuarios/username')]
        );

        if (authoritiesResponse?.data?.authorities) {
          const rolesRaw = authoritiesResponse.data.authorities;
          logger.log('Raw authorities:', rolesRaw);

          try {
            let parsedRoles;
            if (typeof rolesRaw === 'string') {
              parsedRoles = JSON.parse(rolesRaw);
            } else {
              parsedRoles = rolesRaw;
            }

            if (
              Array.isArray(parsedRoles) &&
              parsedRoles.length > 0
            ) {
              dispatch(setRoles(true));
              const nombres: string[] = [];
              const links: string[] = [];

              let auth;
              if (typeof parsedRoles[0]?.authority === 'string') {
                auth = JSON.parse(parsedRoles[0].authority);
              } else {
                auth = parsedRoles[0]?.authority || {};
              }

              if (auth?.menu && Array.isArray(auth.menu)) {
                auth.menu.forEach((rol: any) => {
                  if (rol.name) nombres.push(rol.name);
                  if (rol.sub && Array.isArray(rol.sub)) {
                    rol.sub.forEach((rolSub: any) => {
                      if (rolSub.acro) nombres.push(rolSub.acro);
                      if (rolSub.link) links.push(rolSub.link);
                    });
                  }
                });
              }
              dispatch(setPermisos(nombres));
              dispatch(setLinkPermisos(links));
            }
          } catch (parseError) {
            logger.error('Error parsing authorities:', parseError);
          }
        }

        if (userResponse?.data) {
          dispatch(onLogin(userResponse.data));

          // Mantener compatibilidad con localStorage
          localStorage.setItem(
            'login',
            JSON.stringify({
              isAuth: true,
              usuario: userResponse.data,
            })
          );
        }

        dispatch(setAuth(true));
        setIsAuthenticated(true);
        return true;
      } else {
        logger.log('Usuario no autenticado');

        // Si no hay sesión en backend, intentar verificación silenciosa con Cognito (prompt=none)
        const silentChecked = sessionStorage.getItem('silentCheckAttempted');
        if (!silentChecked) {
          logger.log('Intentando verificación silenciosa con Cognito...');
          isRedirecting = true;
          sessionStorage.setItem('silentCheckAttempted', 'true');
          const baseUrl =
            process.env.REACT_APP_API_URL?.replace('/api', '') ||
            'http://localhost:8090';
          window.location.assign(
            `${baseUrl}/oauth2/authorization/cognito?silent=true`
          );
          return null;
        }

        logger.log('Verificación silenciosa ya intentada, mostrando login');
        setIsAuthenticated(false);
        return false;
      }
    } catch (error: any) {
      logger.error('Error in checkAuth:', safeError(error));
      setIsAuthenticated(false);
      return false;
    } finally {
      if (!isRedirecting) {
        setIsInitialized(true);
      }
    }
  }, [dispatch]);

  useEffect(() => {
    const shouldCheckAuth = () => {
      const publicRoutes = [
        '/auth/callback',
        '/register',
        '/forgot-password',
      ];
      const isPublicRoute = publicRoutes.includes(location.pathname);

      logger.log('Current path:', location.pathname);
      logger.log('Is public route:', isPublicRoute);

      // Siempre verificar autenticación, excepto en rutas públicas
      return !isPublicRoute;
    };

    if (shouldCheckAuth()) {
      logger.log('Checking auth on mount...');
      checkAuth();
    } else {
      logger.log('Skipping auth check - public route');
      setIsAuthenticated(false);
      setIsInitialized(true);
    }
  }, [location.pathname, checkAuth]);

  // No renderizar hijos hasta que la verificación esté completa
  if (!isInitialized) {
    return <div>Verificando autenticación...</div>;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      'useAuthContext must be used within an AuthProvider'
    );
  }
  return context;
};
